import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Paper 
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory } from 'react-router-dom';

const ProductDetailSentinel2 = () => {
  const history = useHistory();
  const [activeLevel, setActiveLevel] = useState('L2A');
  const [mapType, setMapType] = useState('satellite'); // 'street' or 'satellite'

  // Define processing level information based on selected level
  const levelInfo = {
    'L0': {
      radiometricLevel: 'Raw Instrument Data',
      geometricProcessing: 'None',
      description: 'Level-0 products contain raw, unprocessed telemetry data from the Sentinel-2 sensor. These are used internally by ground stations for further processing and are not distributed to end users.',
    },
    'L1A': {
      radiometricLevel: 'Unprocessed Image Data',
      geometricProcessing: 'None',
      description: 'Level-1A products consist of reconstructed, unprocessed image data at full instrument resolution, with appended radiometric and geometric metadata. These are internal and not made available to users.',
    },
    'L1B': {
      radiometricLevel: 'Radiometrically Corrected',
      geometricProcessing: 'Sensor Corrections Applied',
      description: 'Level-1B products are radiometrically corrected (converted to radiance), with sensor-related geometric distortions removed. However, they are not yet georeferenced. This level is part of internal processing only.',
    },
    'L1C': {
      radiometricLevel: 'Top-of-Atmosphere Reflectance',
      geometricProcessing: 'Orthorectified (Map Projection Applied)',
      description: 'Level-1C products are the first user-accessible data level. They provide Top-of-Atmosphere (TOA) reflectance, are orthorectified using a Digital Elevation Model (DEM), and are georeferenced in a standard map projection. Suitable for visualization and basic analysis.',
    },
    'L2A': {
      radiometricLevel: 'Bottom-of-Atmosphere (Surface Reflectance)',
      geometricProcessing: 'Orthorectified',
      description: 'Level-2A products are atmospherically corrected from L1C to provide surface reflectance (BOA). They include scene classification and cloud/shadow masks. Ideal for scientific analysis, vegetation indices, and land cover classification.',
    }
  };
  

  return (
    <Box sx={{ maxWidth: '1200px', mx: 'auto', p: 2 }}>
      {/* Back Navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          sx={{
            color: '#4287f5',
            textTransform: 'none',
            fontWeight: 500
          }}
          onClick={() => history.push('/products')}
        >
          Products &gt; Sentinel - 2
        </Button>
      </Box>

      {/* Product Description */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
          Product Description
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
        Sentinel-2, a cornerstone of the Copernicus Earth Observation program, exemplifies the paradigm of delivering high-value geospatial insights through a multi-mission, user-centric approach. With its twin satellites, Sentinel-2A and 2B, operating in a phased orbit, the mission provides frequent revisits (5-day cycle) and global coverage—maximizing the utility of each pixel captured.

At the heart of Sentinel-2’s value lies a demand-aware sensing philosophy. Equipped with a MultiSpectral Instrument (MSI) featuring 13 spectral bands across visible, near-infrared, and shortwave infrared ranges, Sentinel-2 delivers high-resolution (10–60 m) optical imagery that caters to diverse sectors like agriculture, forestry, disaster management, and environmental monitoring.

Going beyond a generic imagery model, Sentinel-2 supports vertical use cases by integrating calibrated data and derived products (like NDVI, LAI, and land cover classification) directly into actionable decision pipelines. The platform also demonstrates strong capabilities in signal fidelity and radiometric quality, with excellent signal-to-noise ratios and Modulation Transfer Function (MTF) ensuring sharp, accurate imaging.

With a Technology Readiness Level of 9, Sentinel-2 continues to redefine EO standards through its commitment to open data access, precision sensing, and scalable impact across global challenges.
        </Typography>
      </Box>

      {/* Specifications Table */}
      <TableContainer component={Paper} sx={{ mb: 4, border: '1px solid #d0d7de', boxShadow: 'none' }}>
  <Table>
    <TableHead>
      <TableRow sx={{ backgroundColor: '#f0f4fa' }}>
        <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Geographical Coverage</TableCell>
        <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Image Resolution</TableCell>
        <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Source</TableCell>
        <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Revisit Frequency</TableCell>
        <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Data Format</TableCell>
        {/* <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Unit</TableCell> */}
      </TableRow>
    </TableHead>
    <TableBody>
      <TableRow hover>
        <TableCell align="center">Global*</TableCell>
        <TableCell align="center">10 m x 10 m Pixel</TableCell>
        <TableCell align="center">Sentinel - 2</TableCell>
        <TableCell align="center">5-day cycle</TableCell>
        <TableCell align="center">GeoTIFF, .png, .csv</TableCell>
        {/* <TableCell align="center">N/A</TableCell> */}
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ fontStyle: 'italic', fontSize: '0.85rem', color: 'gray', py: 1 }}>
          *Global coverage available. AOI outside India requires additional processing time.
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>

  <Table sx={{ mt: 4 }}>
    <TableHead>
      <TableRow sx={{ backgroundColor: '#f0f4fa' }}>
        <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>Spectral Band</TableCell>
        <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>Wavelength Range</TableCell>
        <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>Description</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
  <TableRow hover>
    <TableCell>Coastal Aerosol</TableCell>
    <TableCell>~ 443 nm (Band 1)</TableCell>
    <TableCell>Used for atmospheric correction and coastal water studies.</TableCell>
  </TableRow>
  <TableRow hover>
    <TableCell>Blue (B)</TableCell>
    <TableCell>~ 490 nm (Band 2)</TableCell>
    <TableCell>Highlights water bodies and atmospheric haze; useful for bathymetry and coastal monitoring.</TableCell>
  </TableRow>
  <TableRow hover>
    <TableCell>Green (G)</TableCell>
    <TableCell>~ 560 nm (Band 3)</TableCell>
    <TableCell>Assesses plant vigor and distinguishes vegetation types.</TableCell>
  </TableRow>
  <TableRow hover>
    <TableCell>Red (R)</TableCell>
    <TableCell>~ 665 nm (Band 4)</TableCell>
    <TableCell>Captures visible red light; essential for vegetation and soil discrimination.</TableCell>
  </TableRow>
  <TableRow hover>
    <TableCell>Red Edge 1</TableCell>
    <TableCell>~ 705 nm (Band 5)</TableCell>
    <TableCell>Detects early plant stress and chlorophyll content.</TableCell>
  </TableRow>
  <TableRow hover>
    <TableCell>Red Edge 2</TableCell>
    <TableCell>~ 740 nm (Band 6)</TableCell>
    <TableCell>Monitors subtle vegetation changes and crop health.</TableCell>
  </TableRow>
  <TableRow hover>
    <TableCell>Red Edge 3</TableCell>
    <TableCell>~ 783 nm (Band 7)</TableCell>
    <TableCell>Further refines vegetation analysis and biomass estimation.</TableCell>
  </TableRow>
  <TableRow hover>
    <TableCell>Near-Infrared (NIR)</TableCell>
    <TableCell>~ 842 nm (Band 8)</TableCell>
    <TableCell>Crucial for vegetation health, canopy structure, and water delineation.</TableCell>
  </TableRow>
  <TableRow hover>
    <TableCell>Narrow NIR</TableCell>
    <TableCell>~ 865 nm (Band 8A)</TableCell>
    <TableCell>Enables finer vegetation structure analysis and atmospheric corrections.</TableCell>
  </TableRow>
  <TableRow hover>
    <TableCell>Water Vapor</TableCell>
    <TableCell>~ 945 nm (Band 9)</TableCell>
    <TableCell>Used for atmospheric correction and water vapor mapping.</TableCell>
  </TableRow>
  <TableRow hover>
    <TableCell>Cirrus</TableCell>
    <TableCell>~ 1375 nm (Band 10)</TableCell>
    <TableCell>Detects high-altitude clouds like cirrus; important for scene screening.</TableCell>
  </TableRow>
  <TableRow hover>
    <TableCell>SWIR 1</TableCell>
    <TableCell>~ 1610 nm (Band 11)</TableCell>
    <TableCell>Analyzes vegetation moisture, soil moisture, and burned areas.</TableCell>
  </TableRow>
  <TableRow hover>
    <TableCell>SWIR 2</TableCell>
    <TableCell>~ 2190 nm (Band 12)</TableCell>
    <TableCell>Used for geological studies, vegetation classification, and snow/ice detection.</TableCell>
  </TableRow>
</TableBody>

    
  </Table>
</TableContainer>

     

      {/* Processing Level Section - Two Columns */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 4 }}>
        {/* Left Column */}
        <Box sx={{ width: { xs: '100%', md: '50%' }, pr: { md: 2 }, mb: { xs: 3, md: 0 } }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            {['L0', 'L1A', 'L1B', 'L1C', 'L2A'].map(level => (
              <Button
                key={level}
                variant={activeLevel === level ? 'contained' : 'outlined'}
                sx={{
                  borderColor: '#ccc',
                  color: activeLevel === level ? 'white' : '#1976d2',
                  bgcolor: activeLevel === level ? '#1976d2' : 'transparent',
                  '&:hover': {
                    bgcolor: activeLevel === level ? '#1565c0' : 'rgba(25, 118, 210, 0.04)'
                  }
                }}
                onClick={() => setActiveLevel(level)}
              >
                {level}
              </Button>
            ))}
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Radiometric processing level:
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {levelInfo[activeLevel].radiometricLevel}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Geometric processing:
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {levelInfo[activeLevel].geometricProcessing}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {levelInfo[activeLevel].description}
          </Typography>
        </Box>

        {/* Right Column - Could add additional content here */}
        
        
      </Box>

      {/* Order Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
        <Button
          variant="contained"
          sx={{
            bgcolor: '#1976d2',
            color: 'white',
            py: 1,
            px: 4,
            '&:hover': {
              bgcolor: '#1565c0'
            }
          }}
        >
          Order Imagery
        </Button>
      </Box>
    </Box>
  );
};

export default ProductDetailSentinel2;